import { AccessController } from '@adornis/abac/core/AccessController.js';
import { ServerContextPolicy } from '@adornis/abac/policies/ServerContextPolicy.js';
import { ADMIN_GRANTS, type Grant, ORG_GRANTS } from './grants.js';
import type { SaraaScopeResult } from './policies/defineSaraaScopePolicy.js';
import { Generate, Organizations, TemplateFields, Templates, Users } from './policies/index.js';

// biome-ignore lint/suspicious/noExplicitAny: scopes differ in their data shapes
export const allScopes: SaraaScopeResult<any, any, any>[] = [Templates, TemplateFields, Organizations, Users, Generate];

/**
 * `ServerContextPolicy` must stay first: the engine is first-allow-wins, and
 * server-context work has to short-circuit before any policy tries to resolve a
 * user.
 *
 * There is no admin policy and no role-management bridge. Platform admin is a
 * field on the user that makes `resolveGrantsFor` return the global grant set, so
 * it needs no policy of its own; and nothing in saraa reads or writes the abac
 * role documents any more. Their GraphQL operations are still registered — the
 * ERM package imports `Roles.js` — but every resolver behind them throws, because
 * `initSaraaAbac` no longer calls `initRoleManagement`.
 */
export const accessController = new AccessController({
  policies: [ServerContextPolicy, ...allScopes.map(scope => scope.policy)],
});

/** Every permission string saraa knows about. */
export const ALL_SARAA_PERMISSIONS: string[] = allScopes.flatMap(scope => scope.permissions);

/** permission → the check names that may be attached to it. */
export const getCheckOptions = (): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const scope of allScopes) Object.assign(result, scope.checkOptions);
  return result;
};

/**
 * Initialises saraa's authorization.
 *
 * Now a no-op kept as a seam: the access controller is built at module scope from
 * pure policy definitions, and nothing needs a datalayer any more. It used to call
 * `initRoleManagement`, which required the Mongo datalayer on the server and threw
 * without one.
 */
export const initSaraaAbac = () => {};

/**
 * Guards against the quietest failure mode in the whole system: a grant that
 * names a check the scope never registered. The policy looks checks up by name and
 * falls through to a denial when one is missing, so a typo costs hours.
 *
 * This used to be a boot-time check on documents that could drift from the code
 * that seeded them. The grant table is now code, so this is a pure assertion the
 * test suite runs — it can only fail if `grants.ts` and `scopes.ts` disagree.
 */
export const assertCheckNamesExist = (grants: readonly Grant[]) => {
  const checkOptions = getCheckOptions();
  const known = new Set(ALL_SARAA_PERMISSIONS);

  for (const grant of grants) {
    if (!known.has(grant.permission)) {
      throw new Error(`[saraa/permissions] unknown permission "${grant.permission}" in the grant table`);
    }
    const available = checkOptions[grant.permission] ?? [];
    for (const check of grant.checks) {
      if (!available.includes(check)) {
        throw new Error(
          `[saraa/permissions] check "${check}" is not registered for permission "${grant.permission}" ` +
            `(available: ${available.join(', ') || 'none'})`,
        );
      }
    }
  }
};

/** Both grant sets, for the assertion above and for the tests. */
export const allGrantSets = (): Array<{ name: string; grants: readonly Grant[] }> => [
  { name: 'admin', grants: ADMIN_GRANTS },
  { name: 'org', grants: ORG_GRANTS },
];
